import { useEffect, useRef, useState } from "react";
import { defaultSeriesName } from "../lib/documentSeries.js";

export type LandTarget = { series?: string; seriesName?: string };

/**
 * ADR-0181 §3.2 — new document, or new version of an existing one?
 *
 * A purpose-built modal rather than `ConfirmDialog`, because the choice and the
 * name are ONE decision with two fields and chaining two dialogs to collect
 * them would be worse (UI_GUIDELINES §1.3 permits a modal for a quick create of
 * 1–3 fields; §2.8 sets the shell rules this follows: role="dialog",
 * aria-modal, overlay dismiss, stopPropagation, minimal content, two actions).
 *
 * The caller does not render this when the study has no series yet — there is
 * only one possible answer then, and asking a question with one answer is the
 * thing §1.7 forbids.
 *
 * The `series_key` is never shown. Identifiers are not user-facing text.
 */
export function LandDocumentDialog(props: {
  filename: string;
  series: Array<{ key: string; name: string }>;
  onCancel: () => void;
  onLand: (target: LandTarget) => void;
}) {
  const [choice, setChoice] = useState("__new__");
  const [name, setName] = useState(() => defaultSeriesName(props.filename));
  const nameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => nameRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, []);

  const isNew = choice === "__new__";
  // A new document needs a name; landing into an existing series does not.
  const canLand = !isNew || name.trim().length > 0;

  const submit = () => {
    if (!canLand) return;
    props.onLand(isNew ? { seriesName: name.trim() } : { series: choice });
  };

  return (
    <div className="modalOverlay" role="presentation" onClick={props.onCancel}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Land ${props.filename}`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape") props.onCancel();
          if (e.key === "Enter" && canLand) submit();
        }}
      >
        <div style={{ fontWeight: 800, marginBottom: 10 }}>Land “{props.filename}”</div>
        <div style={{ display: "grid", gap: 10 }}>
          <label className="field">
            <span className="label">Land as</span>
            <select className="select" value={choice} onChange={(e) => setChoice(e.target.value)}>
              <option value="__new__">A new document</option>
              {props.series.map((s) => (
                <option key={s.key} value={s.key}>
                  New version of “{s.name}”
                </option>
              ))}
            </select>
          </label>
          {isNew ? (
            <label className="field">
              <span className="label">Document name</span>
              <input
                ref={nameRef}
                className="input"
                value={name}
                maxLength={120}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
              />
              <span className="help">
                The name this document keeps across every future version. The filename can change; this does not.
              </span>
            </label>
          ) : (
            // Reserve the space so choosing a series does not make the dialog
            // jump (§1.4 — predictable layout).
            <span className="help">
              The file lands as the next version. Earlier versions stay downloadable.
            </span>
          )}
        </div>
        <div className="actions" style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button className="btn" type="button" onClick={props.onCancel}>
            Cancel
          </button>
          <button className="btn btnPrimary" type="button" disabled={!canLand} onClick={submit}>
            Land
          </button>
        </div>
      </div>
    </div>
  );
}
